import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Plus, Building2, Briefcase, Users, FileText, Trash2, LogOut, Copy, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, copyToClipboard } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

export default function MasterDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/master/tenants");
      setTenants(data);
    } catch { toast.error("Failed to load tenants"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/master/tenants", form);
      toast.success("Company created");
      setShowModal(false);
      setForm({ name: "", admin_name: "", admin_email: "", admin_password: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its data? This cannot be undone.`)) return;
    try { await api.delete(`/master/tenants/${id}`); toast.success("Company deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const copyLink = async (slug) => {
    const url = `${window.location.origin}/careers/${slug}`;
    const ok = await copyToClipboard(url);
    ok ? toast.success("Careers link copied") : toast.error("Couldn't copy the link");
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-[#1D1D1F] flex items-center justify-center">
              <Layers size={18} className="text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="font-extrabold tracking-tight text-[15px] leading-none">HireScope</div>
              <div className="text-[11px] text-[#86868B] mt-0.5">Master Console</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"
            className="flex items-center gap-2 text-sm font-semibold text-[#86868B] hover:text-[#FF3B30] transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Companies</h1>
            <p className="text-[#86868B] mt-1">Manage every tenant on the platform.</p>
          </div>
          <button onClick={() => setShowModal(true)} data-testid="add-tenant-btn"
            className="bg-[#007AFF] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all flex items-center gap-2">
            <Plus size={18} /> New Company
          </button>
        </div>

        {loading ? (
          <div className="text-[#86868B]">Loading…</div>
        ) : tenants.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 p-12 text-center">
            <Building2 size={40} className="mx-auto text-[#C7C7CC] mb-3" strokeWidth={1.5} />
            <p className="font-semibold">No companies yet</p>
            <p className="text-sm text-[#86868B]">Create your first tenant to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {tenants.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }} data-testid="tenant-card"
                className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                    <Building2 size={20} className="text-[#007AFF]" strokeWidth={1.8} />
                  </div>
                  <button onClick={() => remove(t.id, t.name)} data-testid="delete-tenant-btn"
                    className="text-[#C7C7CC] hover:text-[#FF3B30] transition-colors p-1">
                    <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="font-bold text-lg tracking-tight mt-3">{t.name}</h3>
                <p className="text-xs text-[#86868B] mt-0.5">{t.admin?.email}</p>

                <div className="grid grid-cols-3 gap-2 mt-4 mb-4">
                  <Stat icon={Briefcase} value={t.job_count} label="Jobs" />
                  <Stat icon={FileText} value={t.application_count} label="Apps" />
                  <Stat icon={Users} value={t.member_count} label="Team" />
                </div>

                <button onClick={() => copyLink(t.slug)} data-testid="copy-careers-link"
                  className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#007AFF] bg-[#007AFF]/[0.06] hover:bg-[#007AFF]/10 rounded-xl py-2.5 transition-colors">
                  <Copy size={15} /> Copy careers link
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5" onClick={() => setShowModal(false)}>
          <motion.form initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()} onSubmit={create}
            className="bg-white rounded-3xl shadow-xl border border-black/5 p-7 w-full max-w-md" data-testid="tenant-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold tracking-tight">New Company</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-[#86868B] hover:text-[#1D1D1F]"><X size={20} /></button>
            </div>
            <div className="space-y-3.5">
              <Field label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="tenant-name" placeholder="Acme Inc." />
              <div className="pt-1 text-xs font-bold uppercase tracking-wide text-[#86868B]">First Admin</div>
              <Field label="Admin name" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} testId="tenant-admin-name" placeholder="Jane Doe" />
              <Field label="Admin email" type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testId="tenant-admin-email" placeholder="jane@acme.com" />
              <Field label="Admin password" type="password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testId="tenant-admin-password" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={saving} data-testid="create-tenant-submit"
              className="mt-6 w-full bg-[#007AFF] text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0051FF] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Create company
            </button>
          </motion.form>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-[#F5F5F7] rounded-xl py-2.5 flex flex-col items-center">
      <Icon size={15} className="text-[#86868B] mb-1" strokeWidth={1.8} />
      <div className="font-bold text-sm leading-none">{value}</div>
      <div className="text-[10px] text-[#86868B] mt-0.5">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors" />
    </div>
  );
}
